<script lang="ts" setup>
const REPEAT_TIME = 60;

enum VerificationMethodEnum {
  call = 'call',
  code = 'code',
  service = 'service',
}

const props = withDefaults(
  defineProps<{
    phone: string;
    dark?: boolean;
    readonly?: boolean;
    failed?: boolean;
    repeatCode: (flash: boolean) => Promise<unknown>;
    dataTestId?: string;
    codeAttempts?: number;
    callAttempts?: number;
  }>(),
  { codeAttempts: 3, callAttempts: 3 },
);
const emit = defineEmits<{
  (event: 'complete', value: string): void;
  (event: 'input'): void;
  (event: 'change-phone'): void;
  (event: 'repeat-code'): Promise<void>;
}>();

const { t } = useI18n();

const estimatedTime = ref(REPEAT_TIME);
const timerId = ref<NodeJS.Timeout>();
const currentVerificationMethod = ref(
  props.callAttempts
    ? VerificationMethodEnum.call
    : VerificationMethodEnum.code,
);
const isFailedVerificationMessageVisible = ref(false);

const attempts = reactive({
  [VerificationMethodEnum.call]: props.callAttempts,
  [VerificationMethodEnum.code]: props.codeAttempts,
});

const formattedPhone = computed(() =>
  formatPhone(props.phone, PhoneMask.simple),
);

const startTimer = () => {
  estimatedTime.value = REPEAT_TIME;
  timerId.value = setInterval(() => {
    estimatedTime.value -= 1;
    if (estimatedTime.value === 0) clearTimeout(timerId.value);
  }, 1000);
};

const repeatVerification = () => {
  if (attempts[VerificationMethodEnum.call] > 0) {
    currentVerificationMethod.value = VerificationMethodEnum.call;
    attempts[VerificationMethodEnum.call] -= 1;
  } else if (attempts[VerificationMethodEnum.code] > 0) {
    currentVerificationMethod.value = VerificationMethodEnum.code;
    attempts[VerificationMethodEnum.code] -= 1;
  } else currentVerificationMethod.value = VerificationMethodEnum.service;

  if (currentVerificationMethod.value === VerificationMethodEnum.service) {
    isFailedVerificationMessageVisible.value = true;
    return;
  }

  props
    .repeatCode(currentVerificationMethod.value === VerificationMethodEnum.call)
    .then(() => {
      startTimer();
    });
};

const onChangeCode = (value: string) => {
  emit('complete', value);
};

onMounted(() => {
  if (props.callAttempts) attempts[VerificationMethodEnum.call] -= 1;

  startTimer();
});

onBeforeUnmount(() => {
  clearTimeout(timerId.value);
});
</script>

<template>
  <div
    class="profilePhoneCodeForm"
    :class="{ profilePhoneCodeForm_dark: dark }"
    :data-test-id="dataTestId"
  >
    <span class="profilePhoneCodeForm__info">
      {{
        t(
          currentVerificationMethod === VerificationMethodEnum.call
            ? 'call_sent'
            : 'code_sent',
          { phone: formattedPhone },
        )
      }}
      <button
        @click="emit('change-phone')"
        class="profilePhoneCodeForm__change"
      >
        {{ t('edit') }}
      </button>
    </span>

    <BasePhoneCode
      @complete="onChangeCode"
      @input="emit('input')"
      :failed="failed"
      class="profilePhoneCodeForm__phoneCode"
      :dark="dark"
      :readonly="readonly"
      :data-test-id="dataTestId && `${dataTestId}-field`"
    />
    <span v-if="estimatedTime" class="profilePhoneCodeForm__hint">
      {{
        t(
          currentVerificationMethod === VerificationMethodEnum.call
            ? 'call_repeating'
            : 'code_repeating',
          { time: t('second', { n: estimatedTime }) },
        )
      }}
    </span>
    <button
      v-else-if="currentVerificationMethod !== VerificationMethodEnum.service"
      @click="repeatVerification"
      class="profilePhoneCodeForm__repeat"
    >
      {{ t('repeat') }}
    </button>
    <span v-else class="profilePhoneCodeForm__failed">{{ t('failed') }}</span>
  </div>
</template>

<i18n>
ru:
  call_sent: Ожидайте звонок на {phone} — введите последние 4 цифры входящего номера.
  code_sent: Сообщение с кодом отправлено на {phone}.
  second: '{n} секунду | {n} секунды | {n} секунд'
  call_repeating: Повторный звонок можно получить через {time}.
  code_repeating: Повторный код можно отправить через {time}.
  edit: Изменить
  repeat: Запросить еще раз
  failed: Если вы все еще не можете получить нужный код, обратитесь в службу поддержки для получения помощи.

en:
  call_sent: Expect a call on {phone} — enter the last 4 digits of the incoming number.
  code_sent: The code has been sent to {phone}.
  second: '{n} second | {n} seconds'
  call_repeating: You can request another call in {time}.
  code_repeating: You can send the code again in {time}.
  edit: Edit
  repeat: Request again
  failed: If you still can't receive the code, please contact support for assistance.
</i18n>

<style lang="scss">
.profilePhoneCodeForm {
  $self: &;

  display: flex;
  flex-direction: column;
  align-items: center;
  color: #121212;
  width: 100%;

  &_dark {
    color: #ffffff;

    #{$self}__repeat:hover {
      color: #fff;
    }
  }

  &__info {
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.2px;
  }

  &__change {
    padding: 0;
    background: none;
    border: none;
    text-decoration: underline;
    font: inherit;
    color: inherit;
    cursor: pointer;
    transition: opacity 0.3s ease;

    &:hover {
      opacity: 0.7;
    }
  }

  &__phoneCode {
    margin-top: 20px;

    @include mq('sm') {
      margin-top: 24px;
    }
  }

  &__hint {
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.2px;
    color: #c0c0c0;
    margin-top: 32px;
    text-align: center;

    @include mq('sm') {
      max-width: 234px;
    }
  }

  &__repeat {
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.2px;
    color: #c0c0c0;
    margin-top: 32px;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    transition: color 0.3s ease;

    &:hover {
      color: #121212;
    }
  }

  &__failed {
    margin-top: 32px;
    padding: 0;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.2px;
    color: #eb5757;
    display: block;
  }
}
</style>
